import { formatNumber } from "$root/localization/formatters";
import { BigNumberExt } from "$root/third-party-integrations/bigNumber";
import { compareAtPrecision } from "$root/utils/monetary-math";

/** Where a composition's weight total sits against 100 % once rounded to the client precision. */
export type WeightTotalStatus = "exact" | "over" | "under";

/**
 * The editor's rule for "is the weight total 100?": `total` rounded half-up to `precision` (the
 * customer's decimal places) compared with 100. A total that rounds to 100 is 100 — the banner,
 * the Save gates and the Total row all read it from here.
 */
export function weightTotalStatus(total: number, precision: number): WeightTotalStatus {
	const comparison = compareAtPrecision(total, 100, precision);
	if (comparison === 0) {
		return "exact";
	}
	return comparison === 1 ? "over" : "under";
}

/**
 * The grand-total cell of the edit-mode weight column. An `exact` total reads 100 at
 * `displayDecimals` (the user's Decimals). Any other total widens to the fewest extra decimals,
 * up to `precision`, at which it no longer reads 100 — so the cell never shows 100 next to a
 * "higher/lower than 100%" banner.
 */
export function formatWeightTotal({
	total,
	displayDecimals,
	precision,
}: {
	total: number;
	displayDecimals: number;
	precision: number;
}): string {
	if (weightTotalStatus(total, precision) === "exact") {
		return `${formatNumber(100, displayDecimals)}%`;
	}
	const widest = Math.max(displayDecimals, precision);
	let decimals = displayDecimals;
	while (decimals < widest && compareAtPrecision(total, 100, decimals) === 0) {
		decimals++;
	}
	return `${formatNumber(BigNumberExt.roundWithPrecision(total, decimals), decimals)}%`;
}
